import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { Card, CardBody, Spinner } from './components/ui';
import { useSession } from './lib/session';
import { BatchesPage } from './pages/BatchesPage';
import { DashboardPage } from './pages/DashboardPage';
import { InventoryPage } from './pages/InventoryPage';
import { ItemsPage } from './pages/ItemsPage';
import { LocationsPage } from './pages/LocationsPage';
import { LoginPage } from './pages/LoginPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { ProfilePage } from './pages/ProfilePage';
import { AuditPage } from './pages/AuditPage';
import { QualityPage } from './pages/QualityPage';
import { RecipesPage } from './pages/RecipesPage';
import { RecoveryPage } from './pages/RecoveryPage';
import { RolesPage } from './pages/RolesPage';
import { ReportsPage } from './pages/ReportsPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { UsersPage } from './pages/UsersPage';
import type { Capability } from '@gsls/shared';

function FullPageLoader() {
  return (
    <div className="flex min-h-full items-center justify-center">
      <Spinner label="Checking your session…" />
    </div>
  );
}

function ForbiddenPage() {
  return (
    <Card>
      <CardBody>
        <h1 className="text-lg font-bold text-ink">Not permitted</h1>
        <p className="mt-2 text-sm text-ink-body">
          Your role does not include this section. Ask an administrator if you need access.
        </p>
      </CardBody>
    </Card>
  );
}

function NotFoundPage() {
  return (
    <Card>
      <CardBody>
        <h1 className="text-lg font-bold text-ink">Page not found</h1>
        <p className="mt-2 text-sm text-ink-body">
          That route does not exist. Use the sidebar to navigate.
        </p>
      </CardBody>
    </Card>
  );
}

function AdminOnly({ capability, children }: { capability: Capability; children: React.ReactNode }) {
  const { can } = useSession();
  return can(capability) ? <>{children}</> : <ForbiddenPage />;
}

/** Everything inside here requires a session. */
function ProtectedLayout() {
  const { isAuthenticated, isLoading, mustChangePassword } = useSession();
  const location = useLocation();

  if (isLoading) return <FullPageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  /**
   * A temporary password locks the account to the profile page. The API refuses
   * everything else anyway; this keeps the UI from offering actions that will
   * all bounce.
   */
  if (mustChangePassword && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/items" element={<ItemsPage />} />
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/recipes" element={<RecipesPage />} />
        <Route path="/batches" element={<BatchesPage />} />
        <Route path="/recovery" element={<RecoveryPage />} />
        <Route path="/quality" element={<QualityPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/roles"
          element={
            <AdminOnly capability="users.manage">
              <RolesPage />
            </AdminOnly>
          }
        />
        <Route
          path="/audit"
          element={
            <AdminOnly capability="audit.read">
              <AuditPage />
            </AdminOnly>
          }
        />
        <Route
          path="/users"
          element={
            <AdminOnly capability="users.manage">
              <UsersPage />
            </AdminOnly>
          }
        />

        {/* Milestones that have not landed yet get an honest placeholder. */}

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
